import compose from 'lodash/fp/compose';
import { arrayOf, func, number, oneOfType, shape, string } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import { sendCancelInvoiceEmail as sendCancelInvoiceEmailAction } from '../../balance/actions/invoice-actions';
import InvoiceForm from '../../balance/invoices/InvoiceForm';
import { transformForRendering } from '../../balance/invoices/transform-invoice-helpers';
import UpdateInvoiceStatusButton from '../../balance/invoices/UpdateInvoice/UpdateInvoiceStatusButton';
import { isInvoiceStatusReadOnly } from '../../balance/utils';
import { withDocumentTitle } from '../../generic/DocumentTitle';
import EntityLabel from '../../generic/EntityLabel';
import IconLink from '../../generic/IconLink';
import ScopedDrawer, { changelog, comments } from '../../generic/ScopedDrawer';
import { getTooltipButtonWithText } from '../../generic/Tooltip';
import Titled from '../../layout/Titled';
import { detailedInvoiceShape, productShape } from '../../prop-types';
import api from '../../REST/api';
import withApiRequest from '../../REST/with-api-request';
import withResolved from '../../REST/with-resolved';
import HELP_TEXTS from '../help-texts';
import RequestedChanges from '../RequestedChanges';
import StatusBadge from '../StatusBadge';

const ButtonWithTooltip = getTooltipButtonWithText(HELP_TEXTS);
class ShowInvoice extends React.Component {
  constructor(props) {
    super(props);

    this.handleUpdateStatus = this.handleUpdateStatus.bind(this);
    this.state = { currentStatus: this.props.model.status };
  }

  handleUpdateStatus(invoiceId, newStatus, message = undefined) {
    const { onRequest } = this.props;
    const data = { status: newStatus };
    if (message) {
      data.requestedChanges = message;
    }

    return onRequest({
      id: invoiceId,
      data,
    }).then(response => this.setState({ currentStatus: response.status }));
  }

  render() {
    const { model, products, sendCancelInvoiceEmail } = this.props;
    const { opportunity, id } = model;
    const { currentStatus } = this.state;
    const context = { contentType: 'invoice', objectId: model.id };
    const disabled = isInvoiceStatusReadOnly(currentStatus);

    return (
      <Titled
        title={[
          <EntityLabel key={0} id={model.id} resourceType="invoice">
            Invoice
          </EntityLabel>,
          <StatusBadge key={1} className="ml-4" status={currentStatus} />,
          <span key={2} className="ml-3 font-weight-light">
            {model.name}
          </span>,
        ]}
        actions={
          <div>
            <UpdateInvoiceStatusButton
              invoice={model}
              status={currentStatus}
              onUpdateStatus={this.handleUpdateStatus}
              sendCancelInvoiceEmail={sendCancelInvoiceEmail}
            />
            <IconLink
              to={`/opportunities/${opportunity.id}/show?openInvoiceId=${id}`}
              disabled={disabled}
            >
              <ButtonWithTooltip
                className="btn-primary ml-2"
                helpKey="editInvoice"
                section="actionButtons"
                color="primary"
                disabled={disabled}
                showTooltip={disabled}
              >
                Edit Invoice
              </ButtonWithTooltip>
            </IconLink>
          </div>
        }
      >
        <RequestedChanges invoice={model} className="pb-3" />
        <ScopedDrawer
          options={[
            comments({
              contentType: 'opportunity',
              objectId: opportunity.id,
            }),
            changelog(context),
          ]}
        >
          <div className="mb-4">
            <InvoiceForm
              model={transformForRendering(model)}
              products={products}
              readOnly
              showOpportunity
            />
          </div>
        </ScopedDrawer>
      </Titled>
    );
  }
}

ShowInvoice.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  match: shape({
    params: shape({ id: oneOfType([string, number]).isRequired }).isRequired,
  }).isRequired,
  products: arrayOf(productShape.isRequired).isRequired,
  model: detailedInvoiceShape.isRequired,
  onRequest: func.isRequired,
  sendCancelInvoiceEmail: func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  sendCancelInvoiceEmail: (id, cancellationReason) => {
    return dispatch(sendCancelInvoiceEmailAction(id, cancellationReason));
  },
});

export default compose([
  withResolved({ query: api.getProducts, as: 'products' }),
  withResolved({
    query: api.getInvoice,
    deriveQueryArgs: props => ({ id: props.match.params.id }),
    as: 'model',
  }),
  withApiRequest({
    query: api.patchInvoice,
    successMessage: 'Invoice status updated successfully',
    error: 'flash',
  }),
  withDocumentTitle(({ model }) => model.name),
  connect(null, mapDispatchToProps),
])(ShowInvoice);
